import { FORMAT_TIME } from 'constants';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import dayjs from 'dayjs';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Table, { IndeterminateCheckbox } from 'components/Table';
import { ENUM_ROLE_USER, ENUM_SURVEY_STATUS, ENUM_USER_ASSIGN_THERAPIST } from 'constants/enum';
import { useClickOutside } from 'hooks/useClickOutside';
import PopupAssignTherapist from 'pages/Users/List/PopupAssignTherapist/PopupAssignTherapist';
import AppRoutes from 'routes/routes';
import { convertStatusUserAssignTherapist, getAgeFromMonthAndYear, getGenderText, getJapaneseGovernmentName } from 'utils/helper';

const ControlSurveyItem = ({ item, setShowPopupAssignTherapist }) => {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const { me } = useSelector((state) => state.auth);
  const [openControlSurveyItem, setIsOpenControlSurveyItem] = useState(false);
  const controlSurveyItemRef = useRef(null);
  const contentControlSurveyItemRef = useRef(null);

  useClickOutside(controlSurveyItemRef, () => setIsOpenControlSurveyItem(false));

  const handleDetailUser = () => {
    navigator(AppRoutes.usersDetailFn(item.id));
  };

  return (
    <div className="flex-between-center text-sm">
      <div className="relative flex items-center">
        <button type="button" className="w-6 h-8" onClick={() => setIsOpenControlSurveyItem((prev) => !prev)} ref={controlSurveyItemRef}>
          <img src={require('assets/resources/MoreVert.svg').default} alt="more vert icon" width={24} height={24} />
        </button>
        <div
          ref={contentControlSurveyItemRef}
          className={`absolute z-2 right-0 py-1 bg-white shadow-soft-md animation-dropdown ${openControlSurveyItem ? 'show' : ''}`}
        >
          <ul>
            <li
              onClick={() => handleDetailUser()}
              className="px-3 w-48 py-1.25 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
            >
              {t('VIEW_DETAIL_BABY')}
            </li>
            {me?.role === ENUM_ROLE_USER.ADMIN && (
              <>
                <div className="w-full h-[1px] bg-cyan-591"></div>
                <li
                  onClick={() => setShowPopupAssignTherapist({ show: true, child: item })}
                  className="px-3 w-48 py-1.25 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
                >
                  {t('BABY_ASSIGN_THERAPIST')}
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

const SurveyListTable = ({ data, setSort, sort, page, keyword, isFilter, reloadData }) => {
  const { t } = useTranslation();
  const [selectedRows, setSelectedRows] = useState([{ page: page, selected: [] }]);
  const [showPopupAssignTherapist, setShowPopupAssignTherapist] = useState({ show: false, child: null });
  const { currentLanguage } = useSelector((state) => state.language);

  const getDataNoPublic = (table) => {
    const rows = table.getRowModel().rows;
    const result = _.filter(rows, (item) => item.original.status !== ENUM_SURVEY_STATUS.PUBLIC);
    return result;
  };

  const handleRowSelect = (rowSelect) => {
    try {
      setSelectedRows((prevSelected) => {
        // Get item index with page number
        const pageSelectedExists = _.findIndex(selectedRows, { page: page });
        const selectedRowWithPage = selectedRows.concat();

        // Create new object and push to array
        if (pageSelectedExists === -1) {
          return [
            ...prevSelected,
            {
              page: page,
              selected: [rowSelect?.original?.id],
            },
          ];
        }

        const rowIsSelected = selectedRowWithPage[pageSelectedExists]?.selected?.find((row) => row === rowSelect?.original?.id);

        /**
         * If you are select
         * - Delete the selected item from the array
         * - Delete all Object contains lines
         * - Add new object
         */
        if (rowIsSelected) {
          const selectedRemovedRow = selectedRowWithPage[pageSelectedExists].selected.filter((row) => row !== rowSelect.original.id);
          selectedRowWithPage.splice(pageSelectedExists, 1);
          selectedRowWithPage.push({
            page: page,
            selected: selectedRemovedRow,
          });
          return selectedRowWithPage;
        }

        const rowSelected = selectedRowWithPage[pageSelectedExists].selected;
        selectedRowWithPage.splice(pageSelectedExists, 1);
        selectedRowWithPage.push({
          page: page,
          selected: [...rowSelected, rowSelect.original.id],
        });
        return selectedRowWithPage;
      });
      rowSelect.getToggleSelectedHandler();
    } catch {
      // Handle error select
    }
  };

  const handleSelectAll = (table) => {
    try {
      setSelectedRows((prevState) => {
        const dataNoPublic = getDataNoPublic(table);
        const pageSelectedExists = _.findIndex(selectedRows, { page: page });
        const selectedRowWithPage = selectedRows.concat();

        if (pageSelectedExists === -1) {
          return [
            ...prevState,
            {
              page: page,
              selected: dataNoPublic?.map((row) => row?.original?.id),
            },
          ];
        }

        /**
         * If the Select of the page is currently in the Testhall (Condition) state
         * (Condition): Select length is equal to the number of non -public status items on the current page
         * then remove the whole object of that page to become Uncheck.
         */
        if (selectedRowWithPage[pageSelectedExists]?.selected?.length === dataNoPublic?.length) {
          selectedRowWithPage.splice(pageSelectedExists, 1);
          return selectedRowWithPage;
        }

        selectedRowWithPage.splice(pageSelectedExists, 1);
        selectedRowWithPage.push({
          page: page,
          selected: dataNoPublic.map((row) => row?.original?.id),
        });

        return selectedRowWithPage;
      });
    } catch {
      // Handle error select all
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: '-',
        header: ({ table }) => (
          <div className={`${getDataNoPublic(table)?.length <= 0 && '!cursor-not-allowed'} flex justify-center items-center w-4`}>
            <IndeterminateCheckbox
              className={`m-auto  ${getDataNoPublic(table)?.length <= 0 && '!pointer-events-none'}`}
              {...{
                checked: _.find(selectedRows, { page: page })?.selected?.length === getDataNoPublic(table)?.length && data?.length > 0,
                indeterminate: false,
                onChange: () => handleSelectAll(table),
              }}
            />
          </div>
        ),
        size: 48,
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <div className="flex justify-center">
              <IndeterminateCheckbox
                className={`m-auto ${row.original.status !== ENUM_SURVEY_STATUS.PUBLIC ? '' : 'opacity-50 !cursor-not-allowed'}`}
                {...{
                  checked:
                    row?.original?.status !== ENUM_SURVEY_STATUS.PUBLIC
                      ? _.find(selectedRows, { page: page })?.selected?.some((item) => item === row?.original?.id)
                      : false,
                  indeterminate: false,
                  onChange: () => row?.original?.status !== ENUM_SURVEY_STATUS.PUBLIC && handleRowSelect(row),
                }}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'id',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>{t('BABY_ID')}</span>;
        },
        cell: ({ row, getValue }) => {
          return <p>{getValue()}</p>;
        },
        size: 144,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        id: 'user.email',
        accessorKey: 'user.email',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>{t('EMAIL_ADDRESS')}</span>;
        },
        cell: ({ row, getValue }) => {
          return <p>{getValue()}</p>;
        },
        size: 335,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        accessorKey: 'name',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>{t('NAME')}</span>;
        },
        cell: ({ row, getValue }) => {
          return <p>{getValue()}</p>;
        },
        size: 144,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        accessorKey: 'status',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>{t('STATUS_ASSIGN_THERAPIST')}</span>;
        },
        cell: ({ row, getValue }) => {
          const isAssigned = row?.original?.admin !== null;
          const status_name = isAssigned ? ENUM_USER_ASSIGN_THERAPIST.ASSIGNED : ENUM_USER_ASSIGN_THERAPIST.NOT_ASSIGNED;
          return (
            <div className="flex">
              <span
                className={`text-xs rounded-sm flex-middle-center border border-solid min-w-13 min-h-5.75 px-0.5 ${convertStatusUserAssignTherapist(status_name, t)?.class}`}
              >
                {convertStatusUserAssignTherapist(status_name, t).name}
              </span>
            </div>
          );
        },
        size: 144,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        id: 'user.province_code',
        accessorKey: 'user.province_code',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>{t('BABY_GOVERNMENT')}</span>;
        },
        cell: ({ row, getValue }) => {
          const japaneseGovernment = getJapaneseGovernmentName(getValue(), currentLanguage);
          return <p>{japaneseGovernment}</p>;
        },
        size: 144,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        id: 'age',
        accessorKey: '-',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>{t('AGE')}</span>;
        },
        cell: ({ row, getValue }) => {
          const age = getAgeFromMonthAndYear(row?.original?.month, row?.original?.year, t, currentLanguage);
          return <p>{age}</p>;
        },
        size: 112,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        accessorKey: 'gender',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>{t('GENDER')}</span>;
        },
        cell: ({ row, getValue }) => {
          return <p>{getGenderText(getValue(), t)}</p>;
        },
        size: 112,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        id: 'admin.name',
        accessorKey: '-',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>{t('BABY_NAME_THERAPIST')}</span>;
        },
        cell: ({ row, getValue }) => {
          if (!row?.original?.therapists) return <p>-</p>;
          return row?.original?.therapists.map((item, index) => {
            return <p key={index}>{item?.name}</p>;
          });
        },
        size: 144,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        accessorKey: 'last_date_evaluation',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>{t('REPORT_SENT_DATE')}</span>;
        },
        cell: ({ row, getValue }) => {
          return <p>{getValue() ? dayjs(getValue()).format(FORMAT_TIME.DATE) : '-'}</p>;
        },
        size: 144,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        accessorKey: '-',
        header: () => <span></span>,
        enableSorting: false,
        size: 80,
        cell: ({ row }) => <ControlSurveyItem item={row?.original || {}} setShowPopupAssignTherapist={setShowPopupAssignTherapist} />,
      },
    ],
    [selectedRows, page, t]
  );

  useEffect(() => {
    setSelectedRows([]);
  }, [sort, keyword]);

  return (
    <>
      <Table columns={columns} data={data} isFilter={isFilter} />
      {showPopupAssignTherapist.show && (
        <PopupAssignTherapist
          reloadData={reloadData}
          child={showPopupAssignTherapist.child}
          closePopup={() => setShowPopupAssignTherapist({ show: false, childId: null })}
        />
      )}
    </>
  );
};

export default React.memo(SurveyListTable);
